import {
  Button,
  ButtonSet,
  Dropdown,
  IconButton,
  InlineNotification,
  SkeletonText,
  Stack,
  StructuredListBody,
  StructuredListCell,
  StructuredListHead,
  StructuredListRow,
  StructuredListWrapper,
  Tag,
  TextInput,
  Toggle,
} from '@carbon/react';
import { Add, TrashCan } from '@carbon/icons-react';
import { hasPermission, useAdmin, type RecordValues } from '@pos/admin-ui';
import type { HoComponents } from '@pos/api-client';
import { useTranslation } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import { asProblemMessage } from '../sync/problem';
import './channelStores.scss';

type Channel = HoComponents['schemas']['StoreSalesChannelDto'];
type Window = HoComponents['schemas']['OrderingWindowDto'];

/** ISO weekdays, Monday first. */
const DAYS = [1, 2, 3, 4, 5, 6, 7] as const;

const key = (storeId: string) => ['ho', 'store-sales-channels', storeId] as const;

async function unwrap<T>(call: Promise<{ data?: T; error?: unknown }>): Promise<T> {
  const { data, error } = await call;
  if (data === undefined) throw error;
  return data;
}

const STATUS_TAG: Record<string, 'green' | 'warm-gray' | 'gray'> = { open: 'green', paused: 'warm-gray', closed: 'gray' };

/**
 * A store's sales channels (FR-ONL-07): each channel switched on or off for the store (the POS always on), and for an online
 * channel the windows when it takes orders, several a day if needed (e.g. lunch and dinner), per weekday. No windows: the store's
 * opening hours. Saved at HO and published to the website at once.
 */
export function StoreSalesChannelsTab({ store }: { store: RecordValues }) {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const canManage = hasPermission(session, 'ho.online.channel_stores.manage');
  const storeId = String(store.id);
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const list = useQuery({
    queryKey: key(storeId),
    queryFn: () => unwrap(hoApi.GET('/api/v1/onlineordering/stores/{storeId}/sales-channels', { params: { path: { storeId } } })),
  });
  const replace = (next: Channel) =>
    queryClient.setQueryData<Channel[]>(key(storeId), (rows) => rows?.map((r) => (r.salesChannelId === next.salesChannelId ? next : r)));
  const toggle = useMutation({
    mutationFn: ({ channelId, enabled }: { channelId: string; enabled: boolean }) =>
      unwrap(
        hoApi.PUT('/api/v1/onlineordering/stores/{storeId}/sales-channels/{channelId}', {
          params: { path: { storeId, channelId } },
          body: { enabled },
        }),
      ),
    onSuccess: replace,
  });

  if (list.isLoading) return <SkeletonText paragraph lineCount={4} />;
  if (list.error || !list.data)
    return <InlineNotification kind="error" lowContrast hideCloseButton title={asProblemMessage(t, list.error)} />;
  const channels = list.data;

  return (
    <Stack gap={5} className="pos-sales-channels">
      <p className="pos-sales-channels__intro">{t('salesChannels.intro')}</p>
      {toggle.error && <InlineNotification kind="error" lowContrast title={asProblemMessage(t, toggle.error)} onCloseButtonClick={() => toggle.reset()} />}
      <StructuredListWrapper aria-label={t('salesChannels.title')}>
        <StructuredListHead>
          <StructuredListRow head>
            <StructuredListCell head>{t('salesChannels.channel')}</StructuredListCell>
            <StructuredListCell head>{t('salesChannels.status')}</StructuredListCell>
            <StructuredListCell head>{t('salesChannels.orderingHours')}</StructuredListCell>
            <StructuredListCell head>{t('salesChannels.sells')}</StructuredListCell>
          </StructuredListRow>
        </StructuredListHead>
        <StructuredListBody>
          {channels.map((c) => (
            <StructuredListRow key={c.salesChannelId}>
              <StructuredListCell>
                <strong>{c.name}</strong> <span className="pos-sales-channels__code">{c.code}</span>
              </StructuredListCell>
              <StructuredListCell>
                {c.alwaysOn ? (
                  <Tag type="blue">{t('salesChannels.alwaysOn')}</Tag>
                ) : c.enabled && c.status ? (
                  <Tag type={STATUS_TAG[c.status] ?? 'gray'}>{t(`salesChannels.state.${c.status as 'open'}`)}</Tag>
                ) : (
                  <Tag type="gray">{t('salesChannels.off')}</Tag>
                )}
              </StructuredListCell>
              <StructuredListCell>
                {c.alwaysOn ? (
                  '—'
                ) : (
                  <span className="pos-sales-channels__hours-cell">
                    <span>{c.orderingHours.length ? t('salesChannels.windows', { count: c.orderingHours.length }) : t('salesChannels.sameAsOpening')}</span>
                    {c.enabled && c.channelStoreId && (
                      <Button kind="ghost" size="sm" onClick={() => setEditing(editing === c.salesChannelId ? null : c.salesChannelId)}>
                        {editing === c.salesChannelId ? t('salesChannels.closeHours') : t('salesChannels.editHours')}
                      </Button>
                    )}
                  </span>
                )}
              </StructuredListCell>
              <StructuredListCell>
                <Toggle
                  id={`sales-channel-${c.salesChannelId}`}
                  size="sm"
                  hideLabel
                  labelText={t('salesChannels.sellsOn', { name: c.name })}
                  toggled={c.enabled}
                  disabled={c.alwaysOn || !canManage || toggle.isPending}
                  onToggle={(on) => toggle.mutate({ channelId: c.salesChannelId, enabled: on })}
                />
              </StructuredListCell>
            </StructuredListRow>
          ))}
        </StructuredListBody>
      </StructuredListWrapper>
      {(() => {
        const channel = channels.find((c) => c.salesChannelId === editing);
        return channel ? (
          <OrderingHoursEditor
            key={channel.salesChannelId}
            storeId={storeId}
            channel={channel}
            others={channels.filter((c) => c.salesChannelId !== channel.salesChannelId && !c.alwaysOn && c.orderingHours.length > 0)}
            canManage={canManage}
            onSaved={(next) => {
              replace(next);
              setEditing(null);
            }}
            onCancel={() => setEditing(null)}
          />
        ) : null;
      })()}
    </Stack>
  );
}

/** One channel's ordering windows by weekday, saved together. */
function OrderingHoursEditor({
  storeId,
  channel,
  others,
  canManage,
  onSaved,
  onCancel,
}: {
  storeId: string;
  channel: Channel;
  others: Channel[];
  canManage: boolean;
  onSaved(next: Channel): void;
  onCancel(): void;
}) {
  const { t } = useTranslation();
  const [windows, setWindows] = useState<Window[]>(() => channel.orderingHours.map((w) => ({ ...w })));
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        hoApi.PUT('/api/v1/onlineordering/stores/{storeId}/sales-channels/{channelId}/ordering-hours', {
          params: { path: { storeId, channelId: channel.salesChannelId } },
          body: { windows },
        }),
      ),
    onSuccess: onSaved,
  });
  const change = (index: number, patch: Partial<Window>) => setWindows((all) => all.map((w, i) => (i === index ? { ...w, ...patch } : w)));
  const add = (day: number) => setWindows((all) => [...all, { dayOfWeek: day, opensAt: '10:00', closesAt: '14:00' }]);
  const copyFirstDay = () => {
    const first = windows.filter((w) => w.dayOfWeek === Number(DAYS.find((d) => windows.some((w) => w.dayOfWeek === d))));
    setWindows(DAYS.flatMap((d) => first.map((w) => ({ ...w, dayOfWeek: d }))));
  };

  return (
    <section className="pos-sales-channels__hours" aria-label={t('salesChannels.hoursFor', { name: channel.name })}>
      <h3 className="pos-sales-channels__hours-title">{t('salesChannels.hoursFor', { name: channel.name })}</h3>
      <p className="pos-sales-channels__intro">{t('salesChannels.hoursHelp')}</p>
      {save.error && <InlineNotification kind="error" lowContrast title={asProblemMessage(t, save.error)} onCloseButtonClick={() => save.reset()} />}
      <div className="pos-sales-channels__tools">
        <Button kind="tertiary" size="sm" disabled={!canManage || windows.length === 0} onClick={copyFirstDay}>
          {t('salesChannels.copyFirstDay')}
        </Button>
        <Button kind="tertiary" size="sm" disabled={!canManage || windows.length === 0} onClick={() => setWindows([])}>
          {t('salesChannels.useOpeningHours')}
        </Button>
        {others.length > 0 && (
          <Dropdown
            id={`copy-hours-${channel.salesChannelId}`}
            size="sm"
            titleText={t('salesChannels.copyFrom')}
            hideLabel
            label={t('salesChannels.copyFrom')}
            items={others}
            itemToString={(o: Channel | null) => o?.name ?? ''}
            disabled={!canManage}
            onChange={({ selectedItem }: { selectedItem: Channel | null }) => selectedItem && setWindows(selectedItem.orderingHours.map((w) => ({ ...w })))}
          />
        )}
      </div>
      <div className="pos-sales-channels__days">
        {DAYS.map((day) => (
          <div key={day} className="pos-sales-channels__day">
            <span className="pos-sales-channels__day-name">{t(`md.enums.day_of_week.${day}` as 'md.enums.day_of_week.1')}</span>
            <div className="pos-sales-channels__windows">
              {windows.map((w, index) =>
                w.dayOfWeek !== day ? null : (
                  <div key={index} className="pos-sales-channels__window">
                    <TextInput
                      id={`opens-${channel.salesChannelId}-${index}`}
                      type="time"
                      size="sm"
                      labelText={t('salesChannels.opens')}
                      hideLabel
                      value={w.opensAt}
                      disabled={!canManage}
                      onChange={(e) => change(index, { opensAt: e.target.value })}
                    />
                    <span aria-hidden="true">–</span>
                    <TextInput
                      id={`closes-${channel.salesChannelId}-${index}`}
                      type="time"
                      size="sm"
                      labelText={t('salesChannels.closes')}
                      hideLabel
                      value={w.closesAt}
                      disabled={!canManage}
                      onChange={(e) => change(index, { closesAt: e.target.value })}
                    />
                    <IconButton
                      kind="ghost"
                      size="sm"
                      label={t('salesChannels.removeWindow')}
                      disabled={!canManage}
                      onClick={() => setWindows((all) => all.filter((_, i) => i !== index))}
                    >
                      <TrashCan />
                    </IconButton>
                  </div>
                ),
              )}
              {!windows.some((w) => w.dayOfWeek === day) && (
                <span className="pos-sales-channels__none">
                  {windows.length ? t('salesChannels.closedThisDay') : t('salesChannels.openingHoursThisDay')}
                </span>
              )}
              <Button kind="ghost" size="sm" renderIcon={Add} disabled={!canManage} onClick={() => add(day)}>
                {t('salesChannels.addWindow')}
              </Button>
            </div>
          </div>
        ))}
      </div>
      <ButtonSet>
        <Button kind="secondary" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button kind="primary" disabled={!canManage || save.isPending} onClick={() => save.mutate()}>
          {t('admin.save')}
        </Button>
      </ButtonSet>
    </section>
  );
}
